import { AfterViewInit, Component, signal, TemplateRef, ViewChild, inject, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { BoFormsModule } from '@bo/ng-forms';
import { TableColumn } from '@swimlane/ngx-datatable';
import { Observable } from 'rxjs';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { AppTableComponent } from '../../../../shared/components/table/app-table.component';
import { OffcanvasService } from '../../../../_services/offcanvas-service/offcanvas.service';
import { UpdatePageTitle } from '../../../../store/app/app.actions';
import { AvatarComponent } from '../../../../shared/components/avatar/avatar.component';
import { employeeCardsTableColumns } from '../../../../_services/cards-service/cards-utils';
import { CardsSearchOptions } from '../../../../shared/components/forms/search-field/search-utils';
import { HttpParams } from '@angular/common/http';
import { updateParamsObject } from '../../../../shared/utils/filter-utils';
import { CardsFilterComponent } from '../_components/cards-filter/cards-filter.component';
import { CardsApiService } from '../../../../_services/cards-service/cards-api/cards-api.service';
import { Card } from '../../../../_interfaces/cards.interface';
import { PaginatedResults } from '@bo/ng-api';
import { EmptyIndicatorPipe } from '../../../../shared/pipes/empty-indicator.pipe';
import {
  StatusBadgeComponent,
  statusBadgeDepartments,
} from '../../../../shared/components/status-badge/status-badge.component';
import { TableHeaderTemplateComponent } from '../../../../shared/components/table-header-template/table-header-template.component';
import { IssueCardSelectEmployeeModalComponent } from '../../../../shared/components/modals/issue-card-select-employee-modal/issue-card-select-employee-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToggleSwitchComponent } from '../../../../shared/components/forms/toggle-switch/toggle-switch.component';

@Component({
  selector: 'app-employee-cards',
  imports: [
    BoFormsModule,
    AppTableComponent,
    RouterOutlet,
    DatePipe,
    CurrencyPipe,
    AvatarComponent,
    CardsFilterComponent,
    EmptyIndicatorPipe,
    StatusBadgeComponent,
    TableHeaderTemplateComponent,
    ToggleSwitchComponent,
  ],
  templateUrl: './employee-cards.component.html',
})
export class EmployeeCardsComponent implements OnInit, AfterViewInit {
  readonly store = inject(Store);
  readonly offcanvasService = inject(OffcanvasService);
  readonly cardsApiService = inject(CardsApiService);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly modalService = inject(NgbModal);

  columns: Array<TableColumn> = [];
  queryParams = new HttpParams();
  private readonly columnsInitialized = signal(false);
  protected readonly statusBadgeDepartments = statusBadgeDepartments;
  protected readonly CardsSearchOptions = CardsSearchOptions;

  // Signals for toggle button state
  showHideRemoved = signal<boolean>(true); // Default: hide removed cards
  filterApplied = signal<boolean>(false); // Track if filters are applied

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // TODO: TemplateRef<any> can be fixed by refactoring the @bo/ng-datatable library.
  @ViewChild('firstName') firstName!: TemplateRef<any>;
  @ViewChild('expiryDate') expiryDate!: TemplateRef<any>;
  @ViewChild('cardStatus') cardStatus!: TemplateRef<any>;
  @ViewChild('balance') balance!: TemplateRef<any>;
  @ViewChild('actions') actions!: TemplateRef<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  @ViewChild(AppTableComponent) table!: AppTableComponent<Card>;

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Employee cards'));
    this.offcanvasService.closeAfterNavigationEnd();
  }

  ngOnInit() {
    // Initialise columns early to prevent expression changed error
    this.columns = employeeCardsTableColumns;
  }

  ngAfterViewInit(): void {
    this.setupColumns();
  }

  private setupColumns(): void {
    if (this.columnsInitialized()) return;

    for (const column of this.columns) {
      if (column.prop === 'holder.first_name') column.cellTemplate = this.firstName;
      if (column.prop === 'expiry_date') column.cellTemplate = this.expiryDate;
      if (column.prop === 'status') column.cellTemplate = this.cardStatus;
      if (column.prop === 'balance') column.cellTemplate = this.balance;
      if (column.prop === 'actions') column.cellTemplate = this.actions;
    }

    // Reassign array to trigger change detection in the table
    this.columns = [...this.columns];
    this.columnsInitialized.set(true);
  }

  onViewCard(id: number) {
    this.offcanvasService.offCanvasNavigate(`/cards/employee-cards/view/${id}`);
  }

  getData = (queryParams: HttpParams): Observable<PaginatedResults<Array<Card>>> => {
    // If a filter is applied, ignore the toggle and use getCardPersonal (user controls status via filter)
    if (this.filterApplied()) {
      return this.cardsApiService.getCardPersonal(queryParams);
    }

    // If toggle is ON (hide removed), use getCardPersonalHideRemoved (adds default status filter)
    if (this.showHideRemoved()) {
      return this.cardsApiService.getCardPersonalHideRemoved(queryParams);
    } else {
      // If the toggle is OFF (show all), use getCardPersonal (no automatic status filter)
      return this.cardsApiService.getCardPersonal(queryParams);
    }
  };

  update(params: HttpParams) {
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: updateParamsObject(params),
      })
      .then(() => {});
  }

  onToggleHideRemoved() {
    // If a filter is applied, ignore the toggle
    if (this.filterApplied()) return;

    this.showHideRemoved.set(!this.showHideRemoved());
    this.table?.reloadData();
  }

  onFilterApplied(applied: boolean) {
    this.filterApplied.set(applied);
    // When the filter is cleared, reload data to respect the toggle state
    if (!applied) this.table?.reloadData();
  }

  issueCard() {
    const modalRef = this.modalService.open(IssueCardSelectEmployeeModalComponent, {
      centered: true,
      size: 'md',
    });

    modalRef.closed.subscribe({
      next: (employeeId: string | null) => {
        if (employeeId) {
          const params = new HttpParams().append('issue_card', true);
          this.router
            .navigate([`/employees/edit/${employeeId}`], { queryParams: updateParamsObject(params) })
            .then(() => {});
        }
      },
    });
  }
}
